import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { readingService } from '../services/reading';
import { ReadingArticle, ReadingAttempt, ReadingQuestion, SubmitAnswerResponse } from '../types';

const ReadingArticlePage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { articleId } = useParams<{ articleId: string }>();
  
  const [article, setArticle] = useState<ReadingArticle | null>(null);
  const [attempt, setAttempt] = useState<ReadingAttempt | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [answerResult, setAnswerResult] = useState<SubmitAnswerResponse | null>(null);
  const [answers, setAnswers] = useState<Map<number, SubmitAnswerResponse>>(new Map());
  const [showArticle, setShowArticle] = useState(true);
  const [loading, setLoading] = useState(true);
  const [startTime, setStartTime] = useState<number>(Date.now());

  useEffect(() => {
    loadArticle();
  }, [articleId]);

  const loadArticle = async () => {
    if (!articleId) return;
    
    setLoading(true);
    try {
      const data = await readingService.getArticle(parseInt(articleId));
      setArticle(data);
      
      // Start attempt
      const attemptData = await readingService.startAttempt(parseInt(articleId));
      setAttempt(attemptData);
      setStartTime(Date.now());
    } catch (error) {
      console.error('Error loading article:', error);
      alert('Error loading article');
      navigate('/reading');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!selectedAnswer || !attempt || !article) return;
    
    const currentQuestion = article.questions![currentQuestionIndex];
    
    try {
      const result = await readingService.submitAnswer({
        attempt_id: attempt.id,
        question_id: currentQuestion.id,
        answer: selectedAnswer
      });
      
      setAnswerResult(result);
      
      // Save result
      const newAnswers = new Map(answers);
      newAnswers.set(currentQuestion.id, result);
      setAnswers(newAnswers);
      
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Error submitting answer');
    }
  };

  const handleNextQuestion = () => {
    if (!article) return;
    
    if (currentQuestionIndex < article.questions!.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer('');
      setAnswerResult(null);
    } else {
      // Quiz completed
      navigate('/reading');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-xl text-gray-600">Loading article...</div>
      </div>
    );
  }

  if (!article || !attempt) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-xl text-red-600">Article not found</div>
      </div>
    );
  }

  const currentQuestion = article.questions![currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / article.questions!.length) * 100;
  const correctAnswers = Array.from(answers.values()).filter(a => a.is_correct).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/reading')}
            className="text-indigo-600 hover:text-indigo-800"
          >
            ← Back to Reading
          </button>
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Question {currentQuestionIndex + 1} of {article.questions!.length}
            </p>
            <div className="w-48 bg-gray-200 rounded-full h-2 mt-1">
              <div 
                className="bg-indigo-600 h-2 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
          >
            Logout
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto p-4">
        {/* Article Info */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{article.title}</h1>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                  {article.difficulty_level}
                </span>
                {article.category && (
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                    {article.category}
                  </span>
                )}
                <span className="text-sm text-gray-600">
                  {article.word_count} words · ~{article.estimated_time_minutes} min
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowArticle(!showArticle)}
              className="bg-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              {showArticle ? 'Hide' : 'Show'} Article
            </button>
          </div>

          {/* Article Text */}
          {showArticle && (
            <div className="prose max-w-none border-t pt-4">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {article.content}
              </p>
            </div>
          )}
        </div>

        {/* Question */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4">
            Question {currentQuestionIndex + 1}
          </h2>
          
          <p className="text-lg mb-6">{currentQuestion.question_text}</p>

          {/* Options */}
          <div className="space-y-3 mb-6">
            {['A', 'B', 'C', 'D'].map((option) => {
              const optionText = currentQuestion[`option_${option.toLowerCase()}` as keyof ReadingQuestion] as string;
              const isSelected = selectedAnswer === option;
              const isCorrect = answerResult && answerResult.correct_answer === option;
              const isWrong = answerResult && selectedAnswer === option && !answerResult.is_correct;
              
              return (
                <button
                  key={option}
                  onClick={() => !answerResult && setSelectedAnswer(option)}
                  disabled={!!answerResult}
                  className={`w-full text-left p-4 rounded-lg border-2 transition ${
                    isCorrect 
                      ? 'border-green-500 bg-green-50'
                      : isWrong
                      ? 'border-red-500 bg-red-50'
                      : isSelected
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-300 hover:border-indigo-300'
                  } ${answerResult ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className="font-bold mr-2">{option}.</span>
                  {optionText}
                  {isCorrect && <span className="float-right text-green-600">✓</span>}
                  {isWrong && <span className="float-right text-red-600">✗</span>}
                </button>
              );
            })}
          </div>

          {/* Answer Result */}
          {answerResult && (
            <div className={`p-4 rounded-lg mb-4 ${
              answerResult.is_correct ? 'bg-green-100 border-green-400' : 'bg-red-100 border-red-400'
            } border`}>
              <p className="font-bold mb-2">
                {answerResult.is_correct ? '✅ Correct!' : '❌ Incorrect'}
              </p>
              {answerResult.explanation && (
                <p className="text-sm text-gray-700">
                  <strong>Explanation:</strong> {answerResult.explanation}
                </p>
              )}
            </div>
          )}

          {/* Score Display */}
          <div className="text-center mb-4 text-gray-600">
            Score: {correctAnswers} / {currentQuestionIndex + (answerResult ? 1 : 0)}
          </div>

          {/* Action Buttons */}
          {!answerResult ? (
            <button
              onClick={handleSubmitAnswer}
              disabled={!selectedAnswer}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
            >
              Submit Answer
            </button>
          ) : (
            <button
              onClick={handleNextQuestion}
              className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700"
            >
              {currentQuestionIndex < article.questions!.length - 1 
                ? 'Next Question →' 
                : 'Finish & See Results'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReadingArticlePage;
